<template>
  <div class="payment-method__container">
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <FeedbackSuccess
          v-if="isShowableSuccessFeedback"
          :message="displaySuccessMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="isShowableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <section class="payment-method__section">
      <PageHeadline v-if="!isApp" title="決済方法を選択" />
      <div class="payment-method__attention">
        <p v-if="isErrorMessageFromServerPresent" class="box-list--alert">
          <span v-for="(message, index) in errorMessageFromServer" :key="index">
            {{ message }}
          </span>
        </p>
      </div>
      <div class="payment-method__attention">
        <p v-if="isDisabledAnyPaymentMethods" class="box-list--attention">
          選択しているギフトの中に決済手段が限定されているものがあります
        </p>
      </div>
      <form action="/payment_methods/submit" method="post" @submit="onSubmit">
        <div class="payment-method__credit-cards-container">
          <div class="payment-method__credit-cards">
            <div v-if="isLoadingCreditCards" class="payment-method__loading">
              <SmallLoading />
            </div>
            <CreditCardList
              v-else-if="isNotEmptyCreditCards"
              :selected-payment-method="selectedPaymentMethod"
              :registered-credit-cards="creditCards"
              :disabled="isDisabledCreditCardPaymentMethod"
              :old-selected-credit-card="selectedPaymentMethod"
              @clicked="onClickDeleteCreditCard"
              @input-credit-card="onInputCreditCard"
            />
            <div
              v-if="isShowableAddingCreditCardButton"
              class="payment-method__add-credit-card-area"
            >
              <a
                class="text-link tl-plus-circle--left"
                href="/credit_cards/new"
              >
                クレジットカードを追加する
              </a>
            </div>
          </div>
        </div>
        <div class="payment-method__selectable-payment-methods-container">
          <div class="payment-method__selectable-payment-methods">
            <div
              v-for="selectablePaymentMethod in selectablePaymentMethods"
              :key="selectablePaymentMethod.value"
              class="radio_btn"
              :class="{
                'check-list-radio-btn': !isIncludedDisabledPaymentMethodsList(
                  selectablePaymentMethod.value
                ),
                'check-list-radio-btn-disabled':
                  isIncludedDisabledPaymentMethodsList(
                    selectablePaymentMethod.value
                  )
              }"
            >
              <input
                :id="selectablePaymentMethod.value"
                v-model="selectedPaymentMethod"
                type="radio"
                name="payment_method_form[payment_method]"
                :value="selectablePaymentMethod.value"
                :disabled="
                  isIncludedDisabledPaymentMethodsList(
                    selectablePaymentMethod.value
                  ) || null
                "
              />
              <label :for="selectablePaymentMethod.value" class="radio-label">
                {{ selectablePaymentMethod.name }}
              </label>
            </div>
          </div>
        </div>
        <input type="hidden" name="authenticity_token" :value="csrfToken" />
        <div class="payment-method__submit-button-area">
          <div class="payment-method__submit-button-area-contents">
            <button
              type="submit"
              class="btn btn--primary"
              :disabled="disableSubmitButton || null"
            >
              決定する
            </button>
          </div>
        </div>
      </form>
    </section>
    <TextModal
      v-if="isShowableModal"
      title-text="クレジットカードの削除"
      :caption-text="'選択したクレジットカードの情報を\n削除してもよろしいですか？'"
      primary-button-text="削除する"
      cancel-button-text="閉じる"
      :primary-button-disabled="deleteModalPrimaryButtonDisabled"
      @clicked-modal-cover="closeModal"
      @clicked-primary="deleteCreditCard"
      @clicked-cancel="closeModal"
    />
  </div>
</template>
<script>
import CreditCardList from '../../organism/payment_methods/credit-card-list.vue'
import TextModal from '../../molecules/modals/text-modal.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import SmallLoading from '../../atoms/loadings/SmallLoading.vue'
import PageHeadline from '../../molecules/headlines/PageHeadline.vue'
import { csrfToken } from '@rails/ujs'
import axios from 'axios'
export default {
  components: {
    FeedbackError,
    FeedbackSuccess,
    CreditCardList,
    TextModal,
    SmallLoading,
    PageHeadline
  },
  props: {
    paymentMethodsJson: {
      type: Object,
      required: true
    },
    oldSelectedPaymentMethod: {
      type: String,
      required: true
    },
    disabledPaymentMethods: {
      type: Array,
      required: true
    },
    noticeMessage: {
      type: String,
      required: false,
      default: ''
    },
    errorMessageFromServer: {
      type: Array,
      required: false,
      default: () => []
    },
    isApp: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      creditCards: [],
      isLoadingCreditCards: false,
      selectablePaymentMethods: [],
      csrfToken: '',
      selectedPaymentMethod: '',
      targetCreditCardCustCode: '',
      isShowableModal: false,
      isShowableErrorFeedback: false,
      errorMessage: '',
      isShowableSuccessFeedback: false,
      isSubmitted: false,
      deleteModalPrimaryButtonDisabled: false,
      localSuccessMessage: ''
    }
  },
  computed: {
    isShowableAddingCreditCardButton() {
      return (
        this.creditCards.length < 3 && !this.isDisabledCreditCardPaymentMethod
      )
    },
    disableSubmitButton() {
      return (
        this.selectedPaymentMethod.length === 0 ||
        this.isIncludedDisabledPaymentMethodsList(this.selectedPaymentMethod) ||
        this.isLoadingCreditCards ||
        this.isSubmitted
      )
    },
    isDisabledCreditCardPaymentMethod() {
      return this.isIncludedDisabledPaymentMethodsList('credit3d')
    },
    isDisabledAnyPaymentMethods() {
      return this.disabledPaymentMethods.length > 0
    },
    isNotEmptyCreditCards() {
      return this.creditCards.length > 0
    },
    isErrorMessageFromServerPresent() {
      return this.errorMessageFromServer.length > 0
    },
    displaySuccessMessage() {
      return this.localSuccessMessage || this.noticeMessage
    }
  },
  mounted() {
    this.getCreditCards()
    this.selectablePaymentMethods = this.paymentMethodsJson.data.map(v => ({
      name: v.attributes.name,
      value: v.attributes.key
    }))
    this.csrfToken = csrfToken()
    this.selectedPaymentMethod = this.oldSelectedPaymentMethod
      ? this.oldSelectedPaymentMethod
      : ''
    if (this.isIncludedDisabledPaymentMethodsList(this.selectedPaymentMethod)) {
      this.selectedPaymentMethod = ''
    }
    this.isShowableSuccessFeedback = this.noticeMessage.length !== 0

    if (this.isErrorMessageFromServerPresent) {
      this.errorMessage = 'エラーが発生しました'
      this.isShowableErrorFeedback = true
    }
  },
  methods: {
    async getCreditCards() {
      this.isLoadingCreditCards = true
      try {
        const res = await axios.get('/api/v1/pickup/credit_cards')
        this.creditCards = res.data
          .map(data => JSON.parse(data))
          .map(credit_card => {
            return {
              custCode: credit_card.cust_code,
              creditCardNumber: credit_card.cc_number,
              creditCardBrand: credit_card.card_brand,
              expiredAt: credit_card.cc_expiration
            }
          })
      } catch (error) {
        console.error(error)
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isLoadingCreditCards = false
    },
    async deleteCreditCard() {
      this.deleteModalPrimaryButtonDisabled = true
      this.selectedPaymentMethod = ''
      try {
        await axios.delete(
          `/api/v1/pickup/credit_cards/${this.targetCreditCardCustCode}`
        )
        this.creditCards = this.creditCards.filter(v => {
          return v.custCode !== this.targetCreditCardCustCode
        })
        // Show success feedback after successful deletion
        this.localSuccessMessage = 'クレジットカードを削除しました'
        this.isShowableSuccessFeedback = true
      } catch (error) {
        console.error(error)
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isShowableErrorFeedback = true
      }
      this.closeModal()
      this.deleteModalPrimaryButtonDisabled = false
    },
    closeModal() {
      this.targetCreditCardCustCode = ''
      this.isShowableModal = false
    },

    onClickDeleteCreditCard(creditCardCustCode) {
      this.targetCreditCardCustCode = creditCardCustCode
      this.isShowableModal = true
    },
    onInputCreditCard(value) {
      this.selectedPaymentMethod = value
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.isShowableErrorFeedback = false
      }, 3000)
    },
    enterSuccessFeedback() {
      setTimeout(() => {
        this.isShowableSuccessFeedback = false
      }, 3000)
    },
    onSubmit() {
      this.isSubmitted = true
    },
    isIncludedDisabledPaymentMethodsList(paymentMethod) {
      return this.disabledPaymentMethods.some(v => v === paymentMethod)
    }
  }
}
</script>
